import React, {useState, useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import Button from '@/shared/components/Button';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import PageHeader from '@/shared/layout/PageHeader';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useAppSelector, useAppDispatch} from '@/shared/hooks/redux';
import {selectCurrentPatient, setCurrentPatient} from '@/slices/patientSlice';
import {User, Lock, Smartphone, Save, Eye, EyeOff, Tag, HelpCircle, X} from 'lucide-react';
import UserService from '@/shared/services/userService';
import type {PointsLedgerRow, UserCoupon, CouponOffer} from '@/shared/services/userService';

interface UserData {
    id: number;
    elis_id: string;
    firstName: string;
    lastName: string;
    birthday: string;
    gender: string;
    ethnicity: string;
    email: string;
    phone: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    twoFAMode?: string;
    smsEnabled: boolean;
}

interface PasswordData {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

interface PasswordErrors {
    passwordLength: boolean;
    passwordLetters: boolean;
    passwordNumber: boolean;
    passwordMatch: boolean;
}

const Settings: React.FC = () => {
    const {user} = useAuth();
    const dispatch = useAppDispatch();
    const currentPatient = useAppSelector(selectCurrentPatient);
    const [searchParams] = useSearchParams();
    const [activeSection, setActiveSection] = useState(() => searchParams.get('section') ?? 'personal');

    // Initialize user data from existing auth state (like old implementation)
    const initialUserData: UserData = {
        id: currentPatient?.id || 0,
        elis_id: user?.userInfo?.elis_id || '',
        firstName: currentPatient?.firstName || '',
        lastName: currentPatient?.lastName || '',
        birthday: currentPatient?.birthdate || '',
        gender: user?.userInfo?.gender || '',
        ethnicity: user?.userInfo?.ethnicity || '',
        email: user?.userInfo?.email || '',
        phone: user?.userInfo?.phone || '',
        address1: user?.userInfo?.address1 || '',
        address2: user?.userInfo?.address2 || '',
        city: user?.userInfo?.city || '',
        state: user?.userInfo?.state || '',
        zip: user?.userInfo?.zip || '',
        country: user?.userInfo?.country || 'United States',
        twoFAMode: user?.userInfo?.twoFAMode || '',
        smsEnabled: user?.userInfo?.smsEnabled || false,
    };

    const [userData, setUserData] = useState<UserData>(initialUserData);
    const [passwordData, setPasswordData] = useState<PasswordData>({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({
        passwordLength: true,
        passwordLetters: true,
        passwordNumber: true,
        passwordMatch: true,
    });
    const [showPasswords, setShowPasswords] = useState({
        old: false,
        new: false,
        confirm: false,
    });
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Update userData when auth state changes (like old implementation)
    useEffect(() => {
        // Only update if we have user data
        if (user?.userInfo || currentPatient) {
            const newUserData = {
                id: currentPatient?.id || user?.userInfo?.id || 0,
                elis_id: user?.userInfo?.elis_id || '',
                firstName: currentPatient?.firstName || user?.userInfo?.firstName || '',
                lastName: currentPatient?.lastName || user?.userInfo?.lastName || '',
                birthday: currentPatient?.birthdate || user?.userInfo?.birthday || '',
                gender: user?.userInfo?.gender || '',
                ethnicity: user?.userInfo?.ethnicity || '',
                email: user?.userInfo?.email || '',
                phone: user?.userInfo?.phone || '',
                address1: user?.userInfo?.address1 || '',
                address2: user?.userInfo?.address2 || '',
                city: user?.userInfo?.city || '',
                state: user?.userInfo?.state || '',
                zip: user?.userInfo?.zip || '',
                country: user?.userInfo?.country || 'United States',
                twoFAMode: user?.userInfo?.twoFAMode || '',
                smsEnabled: user?.userInfo?.smsEnabled || false,
            };

            setUserData(newUserData);
        }
    }, [user, currentPatient]);

    // Match old implementation sections
    const sections = [
        {id: 'personal', label: 'Personal Details', icon: User},
        {id: 'security', label: 'Password & Security', icon: Lock},
        {id: 'points', label: 'Points & Coupons', icon: Tag},
        // SMS Settings only in development (matches old implementation)
        ...(import.meta.env.DEV ? [{id: 'sms', label: 'SMS Settings', icon: Smartphone}] : []),
    ];

    const queryClient = useQueryClient();

    const {data: pointsData, isLoading: pointsLoading} = useQuery({
        queryKey: ['user', 'points'],
        queryFn: () => UserService.getPoints(),
        enabled: activeSection === 'points',
    });

    const [showAllHistory, setShowAllHistory] = useState(false);
    const [showHowPoints, setShowHowPoints] = useState(false);

    const {data: historyData, isLoading: historyLoading} = useQuery({
        queryKey: ['user', 'points', 'history'],
        queryFn: () => UserService.getPointsHistory(50),
        enabled: activeSection === 'points',
    });

    const {data: couponsData, isLoading: couponsLoading} = useQuery({
        queryKey: ['user', 'coupons'],
        queryFn: () => UserService.getCoupons(),
        enabled: activeSection === 'points',
    });

    const {data: offersData, isLoading: offersLoading} = useQuery({
        queryKey: ['user', 'coupons', 'available'],
        queryFn: () => UserService.getAvailableCoupons(),
        enabled: activeSection === 'points',
    });

    const claimMutation = useMutation({
        mutationFn: (offer: CouponOffer) => UserService.claimCoupon(offer),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['user', 'coupons']});
            queryClient.invalidateQueries({queryKey: ['user', 'coupons', 'available']});
            queryClient.invalidateQueries({queryKey: ['user', 'points']});
        },
    });

    // User data update mutation (using centralized API)
    const updateUserMutation = useMutation({
        mutationFn: (data: UserData) => UserService.updateUser(data),
        onSuccess: (_, variables) => {
            // 1. Update local auth state userInfo
            if (user?.userInfo) {
                Object.assign(user.userInfo, {
                    firstName: variables.firstName,
                    lastName: variables.lastName,
                    gender: variables.gender,
                    ethnicity: variables.ethnicity,
                    phone: variables.phone,
                    address1: variables.address1,
                    address2: variables.address2,
                    city: variables.city,
                    state: variables.state,
                    zip: variables.zip,
                    country: variables.country,
                    smsEnabled: variables.smsEnabled,
                    birthday: variables.birthday,
                });

                // Update localStorage user data
                try {
                    localStorage.setItem('user', JSON.stringify(user));
                } catch {
                    // Ignore localStorage save errors
                }
            }

            // 2. Update Redux currentPatient state
            const updatedPatient = {
                id: variables.id,
                firstName: variables.firstName,
                lastName: variables.lastName,
                fullName: `${variables.firstName} ${variables.lastName}`,
                birthdate: variables.birthday,
            };
            dispatch(setCurrentPatient(updatedPatient));

            // 3. Update localStorage patient info (for backwards compatibility)
            try {
                localStorage.setItem('patient', JSON.stringify(updatedPatient));
            } catch {
                // Ignore localStorage save errors
            }

            setSuccessMessage('Settings updated successfully!');
            setErrorMessage('');
        },
        onError: () => {
            setErrorMessage('Failed to update settings. Please try again.');
            setSuccessMessage('');
        },
    });

    // Password change mutation (using centralized API)
    const changePasswordMutation = useMutation({
        mutationFn: async (passwords: PasswordData) => {
            // First verify old password
            await UserService.verifyPassword(userData.email, passwords.oldPassword);
            // Then change password
            return await UserService.changePassword(userData.email, passwords.newPassword);
        },
        onSuccess: () => {
            setPasswordData({oldPassword: '', newPassword: '', confirmPassword: ''});
            setPasswordErrors({passwordLength: true, passwordLetters: true, passwordNumber: true, passwordMatch: true});
            setSuccessMessage('Password updated successfully!');
            setErrorMessage('');
        },
        onError: () => {
            setErrorMessage('Failed to update password. Please try again.');
            setSuccessMessage('');
        },
    });

    const handleUserDataChange = (field: keyof UserData, value: string | boolean) => {
        setUserData((prev) => ({...prev, [field]: value}));

        // Phone formatting like old implementation
        if (field === 'phone' && typeof value === 'string') {
            const digitReg = /^[0-9]$/;
            let formattedValue = value;

            if (value.length === 4 || value.length === 8) {
                if (value[value.length - 1] !== '-') {
                    formattedValue = value.substring(0, value.length - 1) + '-' + value[value.length - 1];
                }
            }

            if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 12)) {
                return; // Don't update if invalid
            }

            setUserData((prev) => ({...prev, phone: formattedValue}));
        }

        // ZIP code validation
        if (field === 'zip' && typeof value === 'string') {
            const digitReg = /^[0-9]$/;
            if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 5)) {
                return;
            }
        }
    };

    const handlePasswordChange = (field: keyof PasswordData, value: string) => {
        setPasswordData((prev) => ({...prev, [field]: value}));

        if (field === 'newPassword') {
            const uppercaseReg = /[A-Z]/;
            const lowercaseReg = /[a-z]/;
            const numberReg = /[\d]/;

            setPasswordErrors((prev) => ({
                ...prev,
                passwordLetters: !uppercaseReg.test(value) || !lowercaseReg.test(value),
                passwordNumber: !numberReg.test(value),
                passwordLength: value.length <= 8,
            }));
        }

        if (field === 'confirmPassword') {
            setPasswordErrors((prev) => ({
                ...prev,
                passwordMatch: value !== passwordData.newPassword,
            }));
        }
    };

    const handleSaveUserData = () => {
        // Phone validation like old implementation
        const phoneReg = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
        if (!phoneReg.test(userData.phone)) {
            setErrorMessage('Invalid phone format, please use ###-###-####');
            return;
        }

        updateUserMutation.mutate(userData);
    };

    const handleChangePassword = () => {
        if (!passwordData.oldPassword) {
            setErrorMessage('Please enter your current password');
            return;
        }

        if (passwordErrors.passwordLength || passwordErrors.passwordLetters || passwordErrors.passwordNumber) {
            setErrorMessage('Please ensure password meets requirements');
            return;
        }

        if (passwordErrors.passwordMatch) {
            setErrorMessage('Please ensure passwords match');
            return;
        }

        changePasswordMutation.mutate(passwordData);
    };

    return (
        <DashboardLayout>
            <PageHeader title="Account Settings" subtitle="Manage your account preferences and security settings" />

            {/* Success/Error Messages */}
            {(successMessage || errorMessage) && (
                <div
                    className={`mb-6 p-4 rounded-lg ${
                        successMessage
                            ? 'bg-mld-success/10 text-mld-success border border-mld-success/20'
                            : 'bg-mld-danger/10 text-mld-danger border border-mld-danger/20'
                    }`}>
                    {successMessage || errorMessage}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:items-start">
                {/* Settings Navigation */}
                <div className="bg-white rounded-2xl shadow-sm border border-mld-panel-border p-6 lg:sticky lg:top-6">
                    <nav className="space-y-2">
                        {sections.map((section) => {
                            const Icon = section.icon;
                            return (
                                <button
                                    key={section.id}
                                    onClick={() => {
                                        setActiveSection(section.id);
                                        setSuccessMessage('');
                                        setErrorMessage('');
                                    }}
                                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors text-left ${
                                        activeSection === section.id
                                            ? 'bg-mld-primary text-white'
                                            : 'text-mld-text-primary hover:bg-gray-50'
                                    }`}>
                                    <Icon className="h-5 w-5" />
                                    <span className="font-medium">{section.label}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Settings Content */}
                <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-mld-panel-border p-6">
                    {activeSection === 'personal' ? (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-mld-text-primary">Personal Details</h3>

                            {/* Basic Information */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-mld-text-primary">Basic Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            First Name
                                        </label>
                                        <input
                                            type="text"
                                            value={userData.firstName}
                                            onChange={(e) => handleUserDataChange('firstName', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Last Name
                                        </label>
                                        <input
                                            type="text"
                                            value={userData.lastName}
                                            onChange={(e) => handleUserDataChange('lastName', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Date of Birth
                                        </label>
                                        <input
                                            type="date"
                                            value={
                                                userData.birthday
                                                    ? new Date(userData.birthday).toISOString().split('T')[0]
                                                    : ''
                                            }
                                            onChange={(e) => handleUserDataChange('birthday', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Biological Sex
                                        </label>
                                        <select
                                            value={userData.gender}
                                            onChange={(e) => handleUserDataChange('gender', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                            <option value="">--</option>
                                            <option value="M">Male</option>
                                            <option value="F">Female</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Ethnicity
                                        </label>
                                        <select
                                            value={userData.ethnicity}
                                            onChange={(e) => handleUserDataChange('ethnicity', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                            <option value="">--</option>
                                            <option value="American Indian or Alaska Native">
                                                American Indian or Alaska Native
                                            </option>
                                            <option value="Asian">Asian</option>
                                            <option value="Black or African American">Black or African American</option>
                                            <option value="Hispanic or Latino">Hispanic or Latino</option>
                                            <option value="Native Hawaiian or Other Pacific Islander">
                                                Native Hawaiian or Other Pacific Islander
                                            </option>
                                            <option value="White">White</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-mld-text-primary">Contact Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={userData.email}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                                            disabled
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Phone
                                        </label>
                                        <input
                                            type="tel"
                                            placeholder="555-555-5555"
                                            value={userData.phone}
                                            onChange={(e) => handleUserDataChange('phone', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Address Information */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-mld-text-primary">Home Address</h4>
                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Street Address
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="123 Apple Street"
                                            value={userData.address1}
                                            onChange={(e) => handleUserDataChange('address1', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Address Line 2 <span className="text-gray-400">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Apt 100"
                                            value={userData.address2}
                                            onChange={(e) => handleUserDataChange('address2', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                                City
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Dallas"
                                                value={userData.city}
                                                onChange={(e) => handleUserDataChange('city', e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                                State
                                            </label>
                                            <select
                                                value={userData.state}
                                                onChange={(e) => handleUserDataChange('state', e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                                <option value="">--</option>
                                                <option value="TX">Texas</option>
                                                <option value="CA">California</option>
                                                <option value="NY">New York</option>
                                                <option value="FL">Florida</option>
                                                {/* Add more states as needed */}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                                ZIP Code
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="10001"
                                                value={userData.zip}
                                                onChange={(e) => handleUserDataChange('zip', e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Country
                                        </label>
                                        <select
                                            value={userData.country}
                                            onChange={(e) => handleUserDataChange('country', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                            <option value="United States">United States</option>
                                            <option value="Canada">Canada</option>
                                            {/* Add more countries as needed */}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleSaveUserData}
                                disabled={updateUserMutation.isPending}
                                className="flex items-center space-x-2 px-6 py-2 bg-mld-primary text-white rounded-lg hover:bg-mld-primary/90 transition-colors disabled:opacity-50">
                                <Save className="h-4 w-4" />
                                <span>{updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}</span>
                            </button>
                        </div>
                    ) : activeSection === 'security' ? (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-mld-text-primary">Password & Security</h3>

                            {/* Password Change */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-mld-text-primary">Change Password</h4>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Current Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPasswords.old ? 'text' : 'password'}
                                                value={passwordData.oldPassword}
                                                onChange={(e) => handlePasswordChange('oldPassword', e.target.value)}
                                                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswords((prev) => ({...prev, old: !prev.old}))}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                {showPasswords.old ? (
                                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-gray-400" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            New Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPasswords.new ? 'text' : 'password'}
                                                value={passwordData.newPassword}
                                                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                                                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswords((prev) => ({...prev, new: !prev.new}))}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                {showPasswords.new ? (
                                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-gray-400" />
                                                )}
                                            </button>
                                        </div>
                                        {passwordData.newPassword && (
                                            <div className="mt-2 space-y-1 text-xs">
                                                <p
                                                    className={
                                                        passwordErrors.passwordLength
                                                            ? 'text-mld-danger'
                                                            : 'text-mld-success'
                                                    }>
                                                    ✓ At least 9 characters long
                                                </p>
                                                <p
                                                    className={
                                                        passwordErrors.passwordLetters
                                                            ? 'text-mld-danger'
                                                            : 'text-mld-success'
                                                    }>
                                                    ✓ Include uppercase, lowercase letters
                                                </p>
                                                <p
                                                    className={
                                                        passwordErrors.passwordNumber
                                                            ? 'text-mld-danger'
                                                            : 'text-mld-success'
                                                    }>
                                                    ✓ Include at least 1 number
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Confirm New Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPasswords.confirm ? 'text' : 'password'}
                                                value={passwordData.confirmPassword}
                                                onChange={(e) =>
                                                    handlePasswordChange('confirmPassword', e.target.value)
                                                }
                                                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowPasswords((prev) => ({...prev, confirm: !prev.confirm}))
                                                }
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                {showPasswords.confirm ? (
                                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-gray-400" />
                                                )}
                                            </button>
                                        </div>
                                        {passwordData.confirmPassword && (
                                            <p
                                                className={`mt-1 text-xs ${passwordErrors.passwordMatch ? 'text-mld-danger' : 'text-mld-success'}`}>
                                                ✓ Passwords match
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={handleChangePassword}
                                    disabled={changePasswordMutation.isPending}
                                    className="px-6 py-2 bg-mld-primary text-white rounded-lg hover:bg-mld-primary/90 transition-colors disabled:opacity-50">
                                    {changePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                                </button>
                            </div>

                            {/* 2FA Section - Only in Development */}
                            {import.meta.env.DEV && (
                                <div className="space-y-4 pt-6 border-t border-gray-200">
                                    <h4 className="font-medium text-mld-text-primary">Two-Factor Authentication</h4>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium text-mld-text-primary">
                                                    2-Step Authentication
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    {userData.twoFAMode ? 'Enabled' : 'Not enabled'}
                                                </p>
                                            </div>
                                            <Button>
                                                {userData.twoFAMode ? 'Manage' : 'Enable'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : activeSection === 'points' ? (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-mld-text-primary">Points & Coupons</h3>
                                <button
                                    onClick={() => setShowHowPoints((v) => !v)}
                                    className="flex items-center gap-1.5 text-sm font-medium text-mld-primary hover:underline">
                                    <HelpCircle className="h-4 w-4" />
                                    How Points Work
                                </button>
                            </div>

                            {/* How Points Work panel */}
                            {showHowPoints && (
                                <div className="rounded-2xl border border-mld-primary/30 bg-mld-primary/5 p-5 relative">
                                    <button
                                        onClick={() => setShowHowPoints(false)}
                                        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
                                        <X className="h-4 w-4" />
                                    </button>
                                    <h4 className="font-semibold text-mld-text-primary mb-3 flex items-center gap-2">
                                        <Tag className="h-4 w-4 text-mld-primary" />
                                        How to Earn Points
                                    </h4>
                                    <ul className="space-y-2.5 text-sm text-gray-600">
                                        <li className="flex items-start gap-2">
                                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-mld-primary text-white text-xs font-bold">1</span>
                                            <span><strong className="text-mld-text-primary">Weekly Login</strong> — Earn 1 point for logging in once per week.</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-mld-primary text-white text-xs font-bold">2</span>
                                            <span><strong className="text-mld-text-primary">Purchase a Test</strong> — Earn 1 point for every dollar spent on a lab test order.</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-mld-primary text-white text-xs font-bold">3</span>
                                            <span><strong className="text-mld-text-primary">Health Improvement</strong> — Earn 5 points each time a biomarker moves into the normal range compared to your previous result.</span>
                                        </li>
                                    </ul>
                                    <div className="mt-4 pt-3 border-t border-mld-primary/20 text-sm text-gray-600">
                                        <strong className="text-mld-text-primary">Redeeming Points</strong> — Use your points to claim discount coupons in the <em>Redeem Points for Coupons</em> section below, then apply them at checkout in the <a href="/shop" className="text-mld-primary hover:underline">shop</a>.
                                    </div>
                                </div>
                            )}

                            {/* Balance summary */}
                            {pointsLoading ? (
                                <div className="text-gray-500 text-sm">Loading...</div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-mld-primary/10 rounded-2xl p-5 text-center">
                                        <p className="text-3xl font-bold text-mld-primary">
                                            {pointsData?.totalPoints ?? 0}
                                        </p>
                                        <p className="text-sm text-gray-500 mt-1">Available Points</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-2xl p-5 text-center">
                                        <p className="text-3xl font-bold text-mld-text-primary">
                                            {pointsData?.lifetimePoints ?? 0}
                                        </p>
                                        <p className="text-sm text-gray-500 mt-1">Lifetime Points</p>
                                    </div>
                                </div>
                            )}

                            {/* History table */}
                            <div>
                                <h4 className="font-medium text-mld-text-primary mb-3">Earning History</h4>
                                {historyLoading ? (
                                    <div className="text-gray-500 text-sm">Loading...</div>
                                ) : !historyData?.history?.length ? (
                                    <p className="text-gray-500 text-sm">No points earned yet.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-gray-200">
                                                    <th className="text-left py-2 pr-4 font-medium text-mld-text-primary">Date</th>
                                                    <th className="text-left py-2 pr-4 font-medium text-mld-text-primary">Description</th>
                                                    <th className="text-left py-2 pr-4 font-medium text-mld-text-primary">Type</th>
                                                    <th className="text-right py-2 font-medium text-mld-text-primary">Points</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(showAllHistory ? historyData.history : historyData.history.slice(0, 8)).map((row: PointsLedgerRow) => (
                                                    <tr key={row.id} className="border-b border-gray-100">
                                                        <td className="py-3 pr-4 text-gray-500">
                                                            {new Date(row.createdAt).toLocaleDateString()}
                                                        </td>
                                                        <td className="py-3 pr-4 text-mld-text-primary">
                                                            {row.description ?? '—'}
                                                        </td>
                                                        <td className="py-3 pr-4">
                                                            <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600 capitalize">
                                                                {row.eventType.replace('_', ' ')}
                                                            </span>
                                                        </td>
                                                        <td className={`py-3 text-right font-medium ${row.points < 0 ? 'text-mld-danger' : 'text-mld-primary'}`}>
                                                            {row.points > 0 ? `+${row.points}` : row.points}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {historyData.history.length > 8 && (
                                            <button
                                                type="button"
                                                onClick={() => setShowAllHistory((v) => !v)}
                                                className="mt-3 text-xs font-medium text-mld-primary hover:underline">
                                                {showAllHistory ? 'Show less' : `Show all ${historyData.history.length} entries`}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Shop Coupons */}
                            <div>
                                <h4 className="text-base font-semibold text-mld-text-primary mb-1 flex items-center gap-2">
                                    <Tag className="h-4 w-4 text-mld-primary" />
                                    Redeem Points for Coupons
                                </h4>
                                <p className="text-xs text-gray-500 mb-3">
                                    You have <span className="font-semibold text-mld-primary">{pointsData?.totalPoints ?? 0} points</span> available.
                                </p>
                                {offersLoading ? (
                                    <div className="text-gray-500 text-sm">Loading...</div>
                                ) : !offersData?.offers?.length ? (
                                    <p className="text-gray-500 text-sm">No coupons available right now.</p>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {offersData.offers.slice(0, 12).map((offer: CouponOffer) => {
                                            const canAfford = (pointsData?.totalPoints ?? 0) >= offer.pointsCost;
                                            const isClaiming =
                                                claimMutation.isPending &&
                                                claimMutation.variables?.discountType === offer.discountType &&
                                                claimMutation.variables?.discountAmount === offer.discountAmount;
                                            return (
                                                <div
                                                    key={`${offer.discountType}-${offer.discountAmount}-${offer.pointsCost}`}
                                                    className="flex flex-col justify-between rounded-xl border border-mld-panel-border bg-white px-4 py-4 gap-3">
                                                    <div>
                                                        <p className="text-lg font-bold text-mld-primary">
                                                            {offer.discountType === 'percentage'
                                                                ? `${offer.discountAmount}% off`
                                                                : `$${Number(offer.discountAmount).toFixed(2)} off`}
                                                        </p>
                                                        {offer.description && (
                                                            <p className="text-xs text-gray-500 mt-0.5">{offer.description}</p>
                                                        )}
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {offer.pointsCost === 0 ? 'Free' : `${offer.pointsCost} pts`}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        disabled={!canAfford || isClaiming}
                                                        onClick={() => claimMutation.mutate(offer)}
                                                        className="w-full rounded-lg bg-mld-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-mld-primary/80 disabled:opacity-40 disabled:cursor-not-allowed">
                                                        {isClaiming ? 'Claiming…' : canAfford ? 'Claim' : 'Not enough pts'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* My Coupons */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-base font-semibold text-mld-text-primary flex items-center gap-2">
                                        <Tag className="h-4 w-4 text-mld-primary" />
                                        My Coupons
                                    </h4>
                                    <a
                                        href="/shop"
                                        className="text-sm font-medium text-mld-primary hover:underline flex items-center gap-1">
                                        Shop →
                                    </a>
                                </div>
                                {couponsLoading ? (
                                    <div className="text-gray-500 text-sm">Loading...</div>
                                ) : !couponsData?.coupons?.length ? (
                                    <p className="text-gray-500 text-sm">No coupons assigned to your account.</p>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {couponsData.coupons.map((coupon: UserCoupon) => (
                                            <a
                                                key={coupon.id}
                                                href="/shop"
                                                className="rounded-xl border border-mld-panel-border bg-white px-4 py-3 flex flex-col gap-1 hover:border-mld-primary hover:shadow-sm transition-all">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-mono text-sm font-semibold text-mld-text-primary">
                                                        {coupon.code}
                                                    </span>
                                                    {coupon.usedOn ? (
                                                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                                                            Used
                                                        </span>
                                                    ) : !coupon.valid ? (
                                                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                                                            Inactive
                                                        </span>
                                                    ) : (
                                                        <span className="rounded-full bg-mld-status-normal/10 px-2 py-0.5 text-xs font-medium text-mld-status-normal">
                                                            Active
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-sm font-medium text-mld-primary">
                                                    {coupon.discountType === 'percentage'
                                                        ? `${coupon.discountAmount}% off`
                                                        : `$${Number(coupon.discountAmount).toFixed(2)} off`}
                                                </span>
                                                {coupon.description && (
                                                    <span className="text-xs text-gray-500">{coupon.description}</span>
                                                )}
                                                {coupon.expiresAt && !coupon.usedOn && (
                                                    <span className="text-xs text-gray-400">
                                                        Expires {new Date(coupon.expiresAt).toLocaleDateString()}
                                                    </span>
                                                )}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : activeSection === 'sms' ? (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-mld-text-primary">SMS Settings</h3>
                            <div className="space-y-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="font-medium text-mld-text-primary">
                                                Test Result SMS Notification
                                            </h4>
                                            <p className="text-sm text-gray-600">
                                                Receive text messages when your test results are available
                                            </p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={userData.smsEnabled}
                                                onChange={(e) => handleUserDataChange('smsEnabled', e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-mld-primary/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-mld-primary"></div>
                                        </label>
                                    </div>
                                </div>
                                <button
                                    onClick={handleSaveUserData}
                                    disabled={updateUserMutation.isPending}
                                    className="px-6 py-2 bg-mld-primary text-white rounded-lg hover:bg-mld-primary/90 transition-colors disabled:opacity-50">
                                    {updateUserMutation.isPending ? 'Saving...' : 'Save SMS Settings'}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default Settings;
